import React, { CSSProperties } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { FiltersBar } from "@/features/orders-analytics/components/FiltersBar";
import { useOrdersAnalyticsData } from "@/features/orders-analytics/hooks/use-orders-analytics-data";
import { useOrdersAnalyticsFilters } from "@/features/orders-analytics/hooks/use-orders-analytics-filters";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { KpiCard } from "@/shell/components/KpiCard";
import { revenueViewModel } from "@/features/orders-analytics/pages/revenue-view-model";
import { timezone } from "@/features/orders-analytics/utils/timezone";

const successCellStyle = (success: string): CSSProperties => ({
    color: success !== "—" && parseFloat(success) < 90 ? "var(--vb-error-500)" : "var(--vb-fg)",
});

export function RevenuePage() {
    const { filters, setFilters } = useOrdersAnalyticsFilters();
    const todayStart = timezone.getTimeZoneDayStartTimestamp(Date.now());
    const { data, loading, isFetching, refresh } = useOrdersAnalyticsData(
        filters,
        "pair",
        todayStart,
        null,
        null
    );

    const vm = revenueViewModel.fromData({
        monetization: data.monetization,
        summary: data.summary,
        customers: data.customers,
        pricingSegmentation: data.segmentation,
    });

    const totalRev = vm.ticketRows.reduce((sum, row) => sum + row.revenueNumeric, 0);

    return (
        <Shell title="Revenue & unit economics" subtitle="How the business monetizes">
            <PageHeader
                title="Revenue & unit economics"
                lede="How the business monetizes. Take rate, margin, ticket distribution, pricing opportunities."
                right={
                    <FiltersBar
                        filters={filters}
                        availableClients={data.availableClients}
                        onFiltersChange={setFilters}
                        onRefresh={refresh}
                        loading={loading || isFetching}
                    />
                }
            />

            <section className="section">
                <SectionHeader
                    title="Revenue economics"
                    count={`${vm.kpis.length} metrics`}
                    meta="MTD · vs previous comparable"
                />
                <div className="grid-4">
                    {vm.kpis.map((kpi) => (
                        <KpiCard
                            key={kpi.label}
                            icon={kpi.icon}
                            label={kpi.label}
                            value={kpi.value}
                            sub={kpi.sub}
                            mom={kpi.mom}
                            tint={kpi.tint}
                        />
                    ))}
                </div>
            </section>

            <section className="section">
                <SectionHeader
                    title="Revenue by ticket range"
                    count={vm.ticketRows.length}
                    meta="how value distributes across order sizes"
                />
                {vm.ticketRows.length === 0 ? (
                    <div className="td-muted" style={emptySectionStyle}>
                        Sin datos disponibles — no hay fuente real para esta sección.
                    </div>
                ) : (
                    <div className="tbl-wrap">
                        <table className="tbl">
                            <thead>
                                <tr>
                                    <th>Ticket range</th>
                                    <th className="td-num">Orders</th>
                                    <th className="td-num">Volume</th>
                                    <th className="td-num">Revenue</th>
                                    <th className="td-num">Take rate</th>
                                    <th className="td-num">Success</th>
                                    <th className="td-num">P95</th>
                                    <th>Share of revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vm.ticketRows.map((row) => {
                                    const share = totalRev > 0 ? (row.revenueNumeric / totalRev) * 100 : 0;
                                    return (
                                        <tr key={row.range}>
                                            <td className="td-strong">{row.range}</td>
                                            <td className="td-num">{row.orders.toLocaleString()}</td>
                                            <td className="td-num">{row.volume}</td>
                                            <td className="td-num td-strong">{row.revenue}</td>
                                            <td className="td-num td-strong">{row.takeRate}</td>
                                            <td className="td-num" style={successCellStyle(row.success)}>
                                                {row.success}
                                            </td>
                                            <td className="td-num">{row.p95}</td>
                                            <td>
                                                <div style={shareCellStyle}>
                                                    <div style={shareBarOuterStyle}>
                                                        <div style={{ ...shareBarInnerStyle, width: `${share}%` }} />
                                                    </div>
                                                    <span className="td-strong num" style={sharePctStyle}>
                                                        {share.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="section">
                <SectionHeader
                    title="Pricing intelligence"
                    count={vm.pricingRows.length}
                    meta="segment take rate · benchmark & opportunity not yet sourced"
                />
                {vm.pricingEmpty ? (
                    <div className="td-muted" style={emptySectionStyle}>
                        Sin datos disponibles — no hay fuente real para esta sección.
                    </div>
                ) : (
                    <div className="tbl-wrap">
                        <table className="tbl">
                            <thead>
                                <tr>
                                    <th>Segment</th>
                                    <th className="td-num">Volume</th>
                                    <th className="td-num">Revenue</th>
                                    <th className="td-num">Current take rate</th>
                                    <th className="td-num">Benchmark</th>
                                    <th className="td-num">Monthly opportunity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vm.pricingRows.map((row) => (
                                    <tr key={row.segment}>
                                        <td className="td-strong">{row.segment}</td>
                                        <td className="td-num">{row.volume}</td>
                                        <td className="td-num">{row.revenue}</td>
                                        <td className="td-num td-strong">{row.takeRate}</td>
                                        <td className="td-num td-muted">{row.benchmark}</td>
                                        <td className="td-num td-muted">{row.opportunity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </Shell>
    );
}

const emptySectionStyle: CSSProperties = { padding: "16px 4px", fontSize: 13 };

const shareCellStyle: CSSProperties = { display: "flex", alignItems: "center", gap: 8 };

const shareBarOuterStyle: CSSProperties = {
    flex: 1,
    height: 6,
    background: "var(--vb-neutral-100)",
    borderRadius: 3,
    overflow: "hidden",
};

const shareBarInnerStyle: CSSProperties = {
    height: "100%",
    background: "var(--vb-primary-500)",
};

const sharePctStyle: CSSProperties = { minWidth: 40, textAlign: "right" };
